import React, { FC, useState } from "react";
import {
  Grid,
  Button,
  Alert,
  Typography,
  CircularProgress,
  Box,
  Zoom,
  useTheme,
} from "@mui/material";
import isEmpty from "validator/lib/isEmpty";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  fetchPlayerFromLicence,
  fetchedErrorMessage,
  fetchedNoPlayer,
  fetchedSearchError,
  getPlayerLoading,
} from "../../features/player/playerSlice";
import {
  getAreOpenedRegistrations,
  getTournoi,
} from "../../features/tournoi/tournoiSlice";
import BackgroundWrapper from "../BackgroundWrapper";
import CustomTextField from "../Base/CustomTextField";

interface RegisterProps {}

const Register: FC<RegisterProps> = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useTheme();

  const [licenceId, setLicenceId] = useState<string>("");
  const [isLicenceValid, setIsLicenceValid] = useState<boolean>(false);
  const [isLicenceDirty, setIsLicenceDirty] = useState<boolean>(false);

  const noPlayer = useAppSelector(fetchedNoPlayer);
  const errorMessage: string = useAppSelector(fetchedErrorMessage);
  const searchError = useAppSelector(fetchedSearchError);
  const areOpenedRegistrations = useAppSelector(getAreOpenedRegistrations);
  const tournoi = useAppSelector(getTournoi);
  const loading = useAppSelector(getPlayerLoading);

  const handleLicenceChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const licenceIdValue: string = e.target.value;
    setLicenceId(licenceIdValue);
    setIsLicenceValid(!isEmpty(licenceIdValue));
  };

  if (!areOpenedRegistrations) {
    return (
      <Zoom in>
        <Box
          sx={{
            maxWidth: 600,
            mx: "auto",
            width: "100%",
            bgcolor: "secondary.light",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 3,
            boxShadow: 2,
            textAlign: "center",
            backgroundImage: `url('/bg-table-tennis.png')`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            zIndex: 1,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: theme.palette.background.default,
              opacity: 0.85,
              zIndex: -1,
            },
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {tournoi?.closedTournamentLabel}
          </Typography>
        </Box>
      </Zoom>
    );
  }

  return (
    <BackgroundWrapper>
      <Zoom in>
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            maxWidth: 600,
            mx: "auto",
            width: "100%",
          }}
        >
          <Grid
            container
            direction="column"
            rowSpacing={4}
            sx={{
              maxWidth: "600px",
              width: "100%",
              mx: "auto",
              p: 2,
            }}
          >
            <Grid item>
              <Box
                sx={{
                  bgcolor: "secondary.light",
                  color: "text.primary",
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: 1,
                }}
              >
                <Typography variant="body1">
                  Afin de pouvoir vous inscrire au tournoi, merci de saisir
                  votre numéro de licence.
                </Typography>
              </Box>
            </Grid>

            <Grid item>
              <CustomTextField
                type="text"
                label="Numéro de Licence"
                autoFocus
                required
                fullWidth
                value={licenceId}
                onChange={handleLicenceChange}
                onBlur={() => setIsLicenceDirty(true)}
                helperText={errorMessage}
                error={(isLicenceDirty && !isLicenceValid) || noPlayer}
              />
            </Grid>

            <Grid item>
              <Button
                variant="contained"
                disabled={!isLicenceValid || loading}
                onClick={async () => {
                  const res = await dispatch(
                    fetchPlayerFromLicence(licenceId)
                  ).unwrap();
                  if (res) {
                    navigate("/register/licence-validation");
                  }
                }}
                color="primary"
                fullWidth
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  Confirmer les informations
                  {loading && (
                    <CircularProgress
                      color="inherit"
                      size={20}
                      sx={{ ml: 2 }}
                    />
                  )}
                </Box>
              </Button>
            </Grid>

            {searchError && (
              <Grid item>
                <Alert severity="error" variant="standard" icon={false}>
                  Erreur lors de la recherche de votre licence. Réessayez plus
                  tard.
                </Alert>
              </Grid>
            )}
          </Grid>
        </Box>
      </Zoom>
    </BackgroundWrapper>
  );
};

export default Register;
